import { ChangeDetectionStrategy, Component, Input, OnDestroy, OnInit } from '@angular/core';
import { HeartbeatGrouping } from '@ng-cloud/badger-core/map/groupings/heartbeat-grouping';
import { IdlePoseGrouping } from '@ng-cloud/badger-core/map/groupings/idle-pose-grouping';
import { MapGrouping } from '@ng-cloud/badger-core/map/groupings/map-grouping';
import { RouteGrouping } from '@ng-cloud/badger-core/map/groupings/route-grouping';
import { MapComponent } from '@ng-cloud/badger-core/map/map.component';
import { MapObject } from '@ng-cloud/badger-core/map/objects/map-object';
import { HasPose } from '@ng-cloud/badger-core/models/interfaces/has-pose';
import { Map } from '@ng-cloud/badger-core/models/map';
import { Robot } from '@ng-cloud/badger-core/models/robot';
import { RobotService } from '@ng-cloud/badger-core/services/robot.service';
import { StoreService } from '@ng-cloud/badger-core/services/store.service';
import { FlexFillDirective, LayoutDirective } from '@ngbracket/ngx-layout/flex';
import moment from 'moment-timezone';
import { Subscription } from 'rxjs';
import { takeWhile } from 'rxjs/operators';

@Component({
  selector: 'bt-live-map',
  templateUrl: './live-map.component.html',
  styleUrls: ['./live-map.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [FlexFillDirective, LayoutDirective, MapComponent]
})
export class LiveMapComponent implements OnInit, OnDestroy {
  @Input() robotId: number;
  @Input() routeLength = 30;
  robot: Robot;
  storeMap: Map;

  mapComponent: MapComponent;
  subscription: Subscription;
  private alive = true;

  canvasOptions: any = {
    defaultCursor: 'pointer',
    allowTouchScrolling: true
  };

  heartbeatGrouping: HeartbeatGrouping = new HeartbeatGrouping({
    hasIndicator: true,
    hasFade: true,
    hasFirstColor: false
  });

  routeGrouping: RouteGrouping = new RouteGrouping(
    [this.heartbeatGrouping as unknown as MapGrouping<HasPose, MapObject>],
    {
      strokeWidth: 6,
      hasFade: true
    }
  );

  idlePoseGrouping: IdlePoseGrouping = new IdlePoseGrouping();

  constructor(
    protected robotService: RobotService,
    protected storeService: StoreService
  ) {
    this.heartbeatGrouping.maximum = this.routeLength;
    this.heartbeatGrouping.changed().subscribe(() => {
      this.mapComponent &&
      this.mapComponent.panAndZoomTo(this.heartbeatGrouping.currentPose());
    });
  }

  ngOnInit() {
    this.robotService.getRobot(this.robotId).subscribe((robot) => {
      this.robot = robot;
      this.storeService.addStore(robot).subscribe();
      this.storeService
        .getMap(robot.storeId)
        .subscribe((map) => (this.storeMap = map));
      this.storeService
        .getPoses(this.robot.storeId, {
          pose_type: 'idle',
          robot_id: this.robot.id
        })
        .subscribe((poses) => this.idlePoseGrouping.addPoses(poses));

      this.subscription = this.robotService
        .heartbeats(this.robotId)
        .pipe(takeWhile(() => this.alive))
        .subscribe((heartbeat) => {
          this.heartbeatGrouping.addData(heartbeat);
        });

      this.robotService
        .getHeartbeats(this.robotId, {
          created_after: moment().subtract(5, 'minutes')
        })
        .subscribe((heartbeats) => {
          this.heartbeatGrouping.setData(heartbeats);
        });
    });
  }

  onMapLoad(mapComponent: MapComponent) {
    this.mapComponent = mapComponent;
    this.mapComponent.addGrouping(this.routeGrouping);
    this.mapComponent.addGrouping(this.heartbeatGrouping);
    this.mapComponent.addGrouping(this.idlePoseGrouping);
    this.mapComponent.render();
  }

  ngOnDestroy() {
    this.subscription && this.subscription.unsubscribe();
    this.alive = false;
  }
}
